'use client';

import { useState, useEffect, useCallback } from 'react';
import { Widget } from '@typeform/embed-react';

interface TypeformItem {
  id: string;
  title: string;
  type: string;
  created_at: string;
  last_updated_at: string;
  settings: { is_public: boolean; is_trial: boolean };
  _links: { display: string; responses: string };
}

interface Answer {
  field: { id: string; type: string; ref: string };
  type: string;
  text?: string;
  email?: string;
  phone_number?: string;
  number?: number;
  boolean?: boolean;
  date?: string;
  choice?: { label: string };
  choices?: { labels: string[] };
}

interface ResponseItem {
  response_id: string;
  token: string;
  submitted_at: string;
  landed_at: string;
  answers?: Answer[];
  metadata?: { browser: string; platform: string };
}

const forms: TypeformItem[] = [
  {
    id: 'JtAlill1',
    title: 'Nail Art Booking & Inquiry',
    type: 'quiz',
    created_at: '2026-03-26T10:52:36.521207Z',
    last_updated_at: '2026-03-26T10:52:36.521207Z',
    settings: { is_public: true, is_trial: false },
    _links: {
      display: 'https://form.typeform.com/to/JtAlill1',
      responses: 'https://api.typeform.com/forms/JtAlill1/responses',
    },
  },
  {
    id: 'dn6ArpY2',
    title: 'My new form',
    type: 'quiz',
    created_at: '2026-03-26T10:21:09.983952Z',
    last_updated_at: '2026-03-26T10:21:09.983952Z',
    settings: { is_public: false, is_trial: false },
    _links: {
      display: 'https://form.typeform.com/to/dn6ArpY2',
      responses: 'https://api.typeform.com/forms/dn6ArpY2/responses',
    },
  },
];

// Field ref → human-readable label map for the Nail Art Booking form
const fieldLabels: Record<string, string> = {
  full_name: 'Full Name',
  email: 'Email',
  phone: 'Phone',
  service_type: 'Service',
  design_inspiration: 'Design Inspiration',
  preferred_date: 'Preferred Date',
  how_did_you_hear: 'How They Heard',
};

function getAnswerValue(answer: Answer): string {
  if (answer.type === 'text') return answer.text ?? '';
  if (answer.type === 'email') return answer.email ?? '';
  if (answer.type === 'phone_number') return answer.phone_number ?? '';
  if (answer.type === 'number') return String(answer.number ?? '');
  if (answer.type === 'boolean') return answer.boolean ? 'Yes' : 'No';
  if (answer.type === 'date') return answer.date ? new Date(answer.date).toLocaleDateString() : '';
  if (answer.type === 'choice') return answer.choice?.label ?? '';
  if (answer.type === 'choices') return answer.choices?.labels?.join(', ') ?? '';
  return '';
}

function getFieldLabel(answer: Answer): string {
  return fieldLabels[answer.field.ref] ?? answer.field.ref ?? answer.field.id;
}

export default function TypeformPage() {
  const [activeTab, setActiveTab] = useState<'forms' | 'embed' | 'responses'>('forms');
  const [selectedFormId, setSelectedFormId] = useState<string>(forms[0].id);
  const [responses, setResponses] = useState<ResponseItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchResponses = useCallback(async (formId: string) => {
    setLoading(true);
    setError(null);
    setResponses([]);
    try {
      const res = await fetch(`/api/typeform/responses?form_id=${formId}&page_size=50`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to fetch responses');
      } else {
        setResponses(data.items ?? []);
        setTotalItems(data.total_items ?? 0);
      }
    } catch {
      setError('Network error while fetching responses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'responses') {
      fetchResponses(selectedFormId);
    }
  }, [activeTab, selectedFormId, fetchResponses]);

  const selectedForm = forms.find((f) => f.id === selectedFormId) ?? forms[0];

  return (
    <main className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-4xl font-semibold text-foreground mb-2">Typeform</h1>
          <p className="text-foreground-soft text-sm">
            Manage and preview your connected Typeform forms.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-border">
          {(['forms', 'embed', 'responses'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-sm font-medium capitalize rounded-t-lg transition-colors ${
                activeTab === tab
                  ? 'bg-surface border border-b-0 border-border text-foreground'
                  : 'text-foreground-soft hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Forms Tab */}
        {activeTab === 'forms' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {forms.map((form) => (
              <div
                key={form.id}
                className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-foreground text-sm">{form.title}</p>
                    <p className="text-xs text-foreground-soft mt-0.5 capitalize">{form.type}</p>
                  </div>
                  <span
                    className={`shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${
                      form.settings.is_public
                        ? 'bg-green-100 text-green-700' :'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {form.settings.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-soft">
                  <span>
                    Updated{' '}
                    {new Date(form.last_updated_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <a
                  href={form._links.display}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Open form ↗
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Embed Tab */}
        {activeTab === 'embed' && (
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Nail Art Booking &amp; Inquiry
            </h2>
            <div
              className="rounded-2xl overflow-hidden border border-border shadow-sm"
              style={{ height: '600px' }}
            >
              <Widget
                id="JtAlill1"
                style={{ width: '100%', height: '100%' }}
                className="w-full h-full"
              />
            </div>
          </div>
        )}

        {/* Responses Tab */}
        {activeTab === 'responses' && (
          <div>
            {/* Form selector */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="text-sm text-foreground-soft font-medium">Form:</span>
              <div className="flex gap-2 flex-wrap">
                {forms.map((form) => (
                  <button
                    key={form.id}
                    onClick={() => setSelectedFormId(form.id)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedFormId === form.id
                        ? 'bg-primary text-white border-primary' :'bg-surface border-border text-foreground-soft hover:text-foreground'
                    }`}
                  >
                    {form.title}
                  </button>
                ))}
              </div>
              <button
                onClick={() => fetchResponses(selectedFormId)}
                className="ml-auto text-xs text-primary hover:underline flex items-center gap-1"
              >
                ↻ Refresh
              </button>
            </div>

            {/* Stats bar */}
            {!loading && !error && (
              <div className="mb-5 flex items-center gap-2">
                <span className="text-sm text-foreground-soft">
                  {totalItems === 0
                    ? 'No responses yet'
                    : `${totalItems} response${totalItems !== 1 ? 's' : ''} total`}
                </span>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Error */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Empty state */}
            {!loading && !error && responses.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-surface border border-border flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-foreground-soft" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-foreground font-medium text-sm mb-1">No responses yet</p>
                <p className="text-foreground-soft text-xs">
                  Responses will appear here once people submit the form.
                </p>
              </div>
            )}

            {/* Response list */}
            {!loading && !error && responses.length > 0 && (
              <div className="flex flex-col gap-3">
                {responses.map((resp, idx) => (
                  <div
                    key={resp.response_id ?? resp.token ?? idx}
                    className="bg-surface border border-border rounded-2xl overflow-hidden"
                  >
                    {/* Row header */}
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background transition-colors"
                      onClick={() =>
                        setExpandedId(
                          expandedId === (resp.response_id ?? resp.token)
                            ? null
                            : resp.response_id ?? resp.token
                        )
                      }
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-medium text-foreground-soft w-6 text-right shrink-0">
                          #{idx + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {resp.answers?.find((a) => a.field.ref === 'full_name')
                              ? getAnswerValue(resp.answers.find((a) => a.field.ref === 'full_name')!)
                              : resp.answers?.find((a) => a.field.ref === 'email')
                              ? getAnswerValue(resp.answers.find((a) => a.field.ref === 'email')!)
                              : `Response ${idx + 1}`}
                          </p>
                          <p className="text-xs text-foreground-soft mt-0.5">
                            Submitted{' '}
                            {new Date(resp.submitted_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <svg
                        className={`w-4 h-4 text-foreground-soft transition-transform shrink-0 ${
                          expandedId === (resp.response_id ?? resp.token) ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Expanded answers */}
                    {expandedId === (resp.response_id ?? resp.token) && (
                      <div className="border-t border-border px-5 py-4">
                        {resp.answers && resp.answers.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                            {resp.answers.map((answer, aIdx) => (
                              <div key={aIdx}>
                                <p className="text-xs font-medium text-foreground-soft mb-0.5">
                                  {getFieldLabel(answer)}
                                </p>
                                <p className="text-sm text-foreground break-words">
                                  {getAnswerValue(answer) || '—'}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-foreground-soft">No answers recorded.</p>
                        )}
                        {resp.metadata && (
                          <div className="mt-4 pt-3 border-t border-border flex gap-4 text-xs text-foreground-soft">
                            <span>Browser: {resp.metadata.browser}</span>
                            <span>Platform: {resp.metadata.platform}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
